import React from 'react';
import { Coins, ArrowRight, Ticket } from 'lucide-react';

export const PointsInfo: React.FC = () => {
  return (
    <section className="pt-12 pb-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 uppercase tracking-wide mb-4">
            Bodovi i <span className="text-go2-pink">Popusti</span>
          </h2>
          <div className="w-24 h-1 bg-go2-yellow mx-auto mb-6"></div>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-16">
          {/* Left Side - Bullets */}
          <div className="lg:w-1/2">
            <div className="bg-gray-50 rounded-3xl p-10 shadow-sm border border-gray-100">
              <ul className="space-y-8">
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-600 mr-6 shadow-sm">
                    <Coins className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">1 EUR = 1 bod</h4>
                    <p className="text-gray-600 leading-relaxed">
                      Jednostavan sistem. Troši više, zaradi više. Svaki evro se računa.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-green-600 mr-6 shadow-sm">
                    <span className="font-bold text-lg">€</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">100 bodova = 5 EUR popusta</h4>
                    <p className="text-gray-600 leading-relaxed">
                      Iskoristi bodove direktno na sledećem putovanju. Nema skrivenih uslova.
                    </p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-go2-blue mr-6 shadow-sm">
                    <span className="font-bold text-lg">24</span>
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">Važe 24 meseca</h4>
                    <p className="text-gray-600 leading-relaxed">
                      Bodovi važe 2 godine od datuma sticanja. Imaš dovoljno vremena da ih iskoristiš.
                    </p>
                  </div>
                </li>
              </ul>

              <div className="mt-10 pt-10 border-t border-gray-200">
                <h4 className="font-bold text-gray-900 mb-6 uppercase tracking-wide text-sm">Maksimalan popust po putovanju:</h4>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-black">
                    <div className="text-xs text-gray-500 uppercase font-bold mb-1">Putnik</div>
                    <div className="font-extrabold text-2xl text-gray-800">20%</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-go2-blue">
                    <div className="text-xs text-go2-blue uppercase font-bold mb-1">Svetski</div>
                    <div className="font-extrabold text-2xl text-go2-blue">30%</div>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-go2-pink">
                    <div className="text-xs text-go2-pink uppercase font-bold mb-1">Legenda</div>
                    <div className="font-extrabold text-2xl text-go2-pink">40%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Example Card */}
          <div className="lg:w-1/2 w-full">
            <div className="bg-gradient-to-br from-go2-blue to-blue-600 rounded-3xl p-8 text-white shadow-2xl relative overflow-hidden transform rotate-1 hover:rotate-0 transition-transform duration-300">
              {/* Decorative circles */}
              <div className="absolute top-0 right-0 -mt-10 -mr-10 w-60 h-60 bg-white opacity-10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-60 h-60 bg-white opacity-10 rounded-full blur-3xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center space-x-3">
                    <div className="bg-white/20 p-2 rounded-lg">
                      <Ticket className="w-6 h-6 text-white" />
                    </div>
                    <span className="font-bold text-lg tracking-wide uppercase">Primer uštede</span>
                  </div>
                  <span className="bg-go2-yellow text-blue-900 text-xs font-bold px-3 py-1 rounded-full uppercase">Svetski Putnik</span>
                </div>
                
                <div className="space-y-6 bg-white/10 rounded-2xl p-6 backdrop-blur-sm border border-white/10">
                  <div className="flex items-center justify-between border-b border-white/20 pb-4">
                    <span className="text-blue-100">Cena putovanja</span>
                    <span className="font-bold text-xl">450 EUR</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span className="text-blue-100">Iskorišćeni bodovi</span>
                      <span className="text-xs text-blue-200">(2000 bodova)</span>
                    </div>
                    <span className="font-bold text-xl text-go2-yellow">-100 EUR</span>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-between">
                  <span className="text-lg font-medium">Tvoja cena:</span>
                  <span className="text-4xl font-extrabold text-white">350 EUR</span>
                </div>
                
                <div className="mt-8 pt-6 border-t border-white/20 text-center">
                  <p className="text-blue-100 text-sm mb-2">Plus zarađuješ nove bodove:</p>
                  <div className="inline-block bg-white text-go2-blue px-4 py-2 rounded-full font-bold shadow-lg">
                    + 420 bodova
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
